// src/services/transactionFetcher/avalancheTransactionFetcher.js
/* global BigInt */
import { TRANSACTION_TYPES } from '../../contexts/TransactionContext';
import { standardizeTransactionFormat } from './transactionFormatUtils';
import secureFetch from '../../utils/secureFetch';

/**
 * Detect transaction type based on method, value, and addresses
 * @param {Object} tx - Transaction data from the API
 * @param {string} walletAddress - Address of the user's wallet
 * @returns {string} - Transaction type from TRANSACTION_TYPES
 */
const detectTransactionType = (tx, walletAddress) => {
  const walletAddressLower = walletAddress.toLowerCase();
  const fromIsWallet = tx.from.toLowerCase() === walletAddressLower;
  const toIsWallet = tx.to && tx.to.toLowerCase() === walletAddressLower;
  
  // Detect approve transactions
  if (tx.method && tx.method.startsWith('approve(')) {
    return TRANSACTION_TYPES.APPROVE;
  }
  
  // Detect swap transactions (SushiSwap, Uniswap, etc.)
  if (tx.method && (
    tx.method.includes('swap') || 
    tx.methodId === '0x5f3bd1c8' ||  // SushiSwap v3 swap
    tx.methodId === '0x38ed1739' ||  // swapExactTokensForTokens
    tx.methodId === '0x7ff36ab5' ||  // swapExactETHForTokens
    tx.methodId === '0x4a25d94a' ||  // swapTokensForExactETH
    tx.methodId === '0x18cbafe5' ||  // swapExactTokensForETH
    tx.methodId === '0xfb3bdb41'     // swapETHForExactTokens
  )) {
    return TRANSACTION_TYPES.SWAP;
  }
  
  // If the transaction is from the wallet but not to the wallet, it's a send
  if (fromIsWallet && !toIsWallet && tx.value !== '0') {
    return TRANSACTION_TYPES.SEND;
  }
  
  // If the transaction is to the wallet but not from the wallet, it's a receive
  if (!fromIsWallet && toIsWallet && tx.value !== '0') {
    return TRANSACTION_TYPES.RECEIVE;
  }
  
  // If there's a complex method call or contract interaction
  if (tx.method && tx.method !== 'transfer(address,uint256)') {
    return TRANSACTION_TYPES.CONTRACT;
  }
  
  // Default for any other transaction
  return TRANSACTION_TYPES.UNKNOWN;
};

/**
 * Map API response item to the app's transaction format
 * @param {Object} tx - Transaction data from the API
 * @param {string} walletAddress - Address of the user's wallet
 * @param {Object} chainConfig - Chain configuration
 * @returns {Object} - Standardized transaction object
 */
const mapTransaction = (tx, walletAddress, chainConfig) => {
  // Ensure we have required fields with sensible defaults
  if (!tx) {
    console.error("Received null transaction object");
    return null;
  }
  
  if (!tx.hash && !tx.id) {
    console.error("Transaction missing hash/id:", tx);
    return null;
  }
  
  // Parse timestamp
  const timestamp = tx.timestamp ? new Date(tx.timestamp).getTime() : Date.now();
  
  // Calculate fee (gasUsed * gasPrice) with safety checks
  const gasUsed = tx.gasUsed || '0';
  const gasPrice = tx.gasPrice || '0';
  let fee = '0';
  try {
    if (gasUsed && gasPrice) {
      fee = (BigInt(gasUsed) * BigInt(gasPrice)).toString();
    }
  } catch (e) {
    console.error("Error calculating fee:", e, { gasUsed, gasPrice });
  }
  
  // Detect transaction type
  const type = detectTransactionType(tx, walletAddress);
  
  // Get token and explorer info from chain config or use defaults
  const tokenSymbol = chainConfig?.nativeTokenSymbol || 'ETH';
  const tokenDecimal = chainConfig?.nativeTokenDecimals || 18;
  const network = chainConfig?.key || 'avalanche';
  const explorerUrl = chainConfig?.explorerUrl 
    ? `${chainConfig.explorerUrl}/tx/${tx.id || tx.hash}` 
    : `https://etherscan.io/tx/${tx.id || tx.hash}`;
  
  // Create transaction object
  return {
    hash: tx.id || tx.hash,
    type,
    from: tx.from || '',
    to: tx.to || '',
    value: tx.value || '0',
    timestamp,
    status: tx.status === true ? 'success' : 'failed',
    tokenSymbol,
    tokenDecimal,
    fee,
    network,
    data: tx.methodId || '',
    methodName: tx.method || '',
    explorerUrl
  };
};

/**
 * Fetch transactions for an avalanche wallet using the Routescan API.
 * Transforms the response to match the expected format in TransactionContext.
 *
 * @param {string} walletAddress - The wallet address to query
 * @param {Object} options - Additional options like limit, nextToken, etc.
 * @param {Object} chainConfig - Chain configuration from supportedChains
 * @returns {Promise<{transactions: Array, hasMore: boolean, nextToken: string}>} - Transactions and pagination info
 */
export const fetchTransactions = async (walletAddress, options = {}, chainConfig = null) => {
  try {
    const { limit = 50, nextToken = null } = options;
    
    // Ensure we have a valid wallet address
    if (!walletAddress || walletAddress.length < 40) {
      console.error("Invalid wallet address:", walletAddress);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    // Base URL
    let url = `https://api.routescan.io/v2/network/mainnet/evm/43114/address/${walletAddress}/transactions?limit=${limit}`;
    
    // Add next token if provided
    if (nextToken) {
      url += `&next=${nextToken}`;
    }
    
    const response = await secureFetch(url);
    
    if (!response.ok) {
      console.error(`API error: ${response.status} - ${response.statusText}`);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    const data = await response.json();
    
    // Check if the response has the expected structure
    if (!data || !Array.isArray(data.items)) {
      console.error("Unexpected API response format:", data);
      return { transactions: [], hasMore: false, nextToken: null };
    }
    
    // Transform the data to match the expected format in TransactionContext
    const mappedTransactions = data.items.map(item => mapTransaction(item, walletAddress, chainConfig));
    
    // Filter out any null mappings
    const transactions = mappedTransactions.filter(tx => tx !== null);
    
    // Get the nextToken from the API response
    const hasMore = !!data.link?.next;
    const nextPageToken = data.link?.nextToken || null;
    
    // Return transactions along with pagination information
    return {
      transactions: standardizeTransactionFormat(transactions),
      hasMore,
      nextToken: nextPageToken
    };
  } catch (error) {
    console.error("Error fetching avalanche transactions:", error);
    return { transactions: [], hasMore: false, nextToken: null };
  }
};